import { create } from "zustand";
import { captureAnalytics, getAnalyticsDistinctId, isAnalyticsCapturing, initAnalytics } from "./analytics";
import { getSupabase, isSupabaseConfigured } from "./supabase/client";
import { firstTouchProperties } from "./first-touch";
import { useAuthStore } from "./auth/store";
import { setSubscriptionPilot } from "./subscription-pilot";
import { getOfferReceipt, readOfferRegion } from "./offer-receipt";
import { FREE_BUILD_VARIANTS, type EquipmentCapVariant } from "./equipment-limit";
import { hasPriorFreeAllowance, preserveFreeAllowance } from "./free-allowance-promise";
import { CONSUMER_OFFER_VERSION, EQUIPMENT_CAP_EXPERIMENT_KEY, setConfirmedOfferAnalytics } from "./offer-analytics-context";

export { CONSUMER_OFFER_VERSION, EQUIPMENT_CAP_EXPERIMENT_KEY };
export type { EquipmentCapVariant };
const VARIANTS: readonly EquipmentCapVariant[] = FREE_BUILD_VARIANTS;
export type OfferStatus = "pending" | "assigned" | "excluded" | "unavailable";
type EquipmentCapExperimentState = {
  ready: boolean;
  status: OfferStatus;
  variant: EquipmentCapVariant | null;
  servedVariant: EquipmentCapVariant | null;
  subject: string | null;
  userId: string | null;
  assignmentId: string | null;
  version: string | null;
  assignedAt: string | null;
};
const EMPTY: EquipmentCapExperimentState = {
  ready: false, status: "pending", variant: null, servedVariant: null, subject: null, userId: null,
  assignmentId: null, version: null, assignedAt: null,
};
export const useEquipmentCapExperiment = create<EquipmentCapExperimentState>(() => ({ ...EMPTY }));

export function useConsumerOffer() {
  const state = useEquipmentCapExperiment();
  const userId = useAuthStore((auth) => auth.user?.id ?? null);
  const current = state.userId === userId &&
    (state.subject === null || state.subject === getAnalyticsDistinctId());
  return { ready: state.ready && current, status: current ? state.status : "pending" as const,
    variant: current ? state.servedVariant : null,
    treatment: current && state.ready && state.servedVariant === "unlimited" };
}
/** The variant actually served to this subject and account, or null while the
 * offer is pending, excluded, unavailable or belongs to a previous identity. */
export function servedFreeBuildVariant(): EquipmentCapVariant | null {
  const state = useEquipmentCapExperiment.getState();
  if (!state.ready || !state.servedVariant) return null;
  if (state.subject !== getAnalyticsDistinctId() || state.userId !== (useAuthStore.getState().user?.id ?? null)) return null;
  return state.servedVariant;
}
export function isUnlimitedFreeBuild(): boolean {
  return servedFreeBuildVariant() === "unlimited";
}
export function isCap6FreeBuild(): boolean {
  return servedFreeBuildVariant() === "cap6";
}
let generation = 0;
const exposedThisPage = new Set<string>();
export function suspendConsumerOffer(): void {
  generation += 1;
  setConfirmedOfferAnalytics(null);
  useEquipmentCapExperiment.setState({ ...EMPTY });
  setSubscriptionPilot("unresolved");
}
export function markConsumerOfferUnavailable(): void {
  // Invalidate pending responses as well as the visible state. A delayed
  // request must not change an offer after startup has already timed out.
  generation += 1;
  setConfirmedOfferAnalytics(null);
  useEquipmentCapExperiment.setState({ ...EMPTY, status: "unavailable",
    userId: useAuthStore.getState().user?.id ?? null });
  setSubscriptionPilot("unavailable");
}
export function excludeConsumerOffer(): void {
  generation += 1;
  setConfirmedOfferAnalytics(null);
  setSubscriptionPilot("ready");
  useEquipmentCapExperiment.setState({ ...EMPTY, ready: true, status: "excluded",
    userId: useAuthStore.getState().user?.id ?? null });
}
/** A server-held receipt, not a cached client flag, owns the offered product.
 * Resolving it again after identify/logout does not randomize that offer. */
export async function resolveConsumerOffer(isEligible: () => boolean): Promise<void> {
  const request = ++generation;
  setConfirmedOfferAnalytics(null);
  useEquipmentCapExperiment.setState({ ...EMPTY });
  setSubscriptionPilot("unresolved");
  await initAnalytics();
  const subject = getAnalyticsDistinctId();
  const userId = useAuthStore.getState().user?.id ?? null;
  const current = () => request === generation && subject === getAnalyticsDistinctId() &&
    userId === (useAuthStore.getState().user?.id ?? null) && isEligible();
  if (!current()) return;
  if (!isSupabaseConfigured) {
    setSubscriptionPilot(import.meta.env.PROD ? "unavailable" : "ready");
    useEquipmentCapExperiment.setState({ ...EMPTY, ready: true, status: "excluded",
    userId: useAuthStore.getState().user?.id ?? null });
    return;
  }
  const token = getOfferReceipt();
  if (!token) {
    markConsumerOfferUnavailable();
    return;
  }
  try {
    const firstSeenAt = firstTouchProperties().arrival_at;
    const [region, preserveAllowance] = await Promise.all([readOfferRegion(token), hasPriorFreeAllowance(firstSeenAt)]);
    if (!current()) return;
    const { data, error } = await getSupabase().functions.invoke("resolve-offer", {
      body: { token, distinctId: subject, firstSeenAt, offerVersion: CONSUMER_OFFER_VERSION, preserveFreeAllowance: preserveAllowance,
        captureAllowed: isAnalyticsCapturing() && !!subject, region },
    });
    if (!current()) return;
    if (data?.identityConflict) {
      captureAnalytics("offer_assignment_conflict", { experiment_key: EQUIPMENT_CAP_EXPERIMENT_KEY,
        experiment_version: CONSUMER_OFFER_VERSION, assignment_id: data.assignmentId });
      throw new Error("Offer identity conflict");
    }
    if (error || !data || (data.status !== "assigned" && data.status !== "excluded")) throw error ?? new Error("Invalid offer response");
    const variant = VARIANTS.find((candidate) => candidate === data.variant) ?? null;
    const supportedVersion = data.version === CONSUMER_OFFER_VERSION ||
      (data.version === "consumer-offer-v1" && variant !== "cap5");
    if (data.status === "assigned" && (!variant || !supportedVersion ||
      typeof data.assignmentId !== "string" || !Number.isFinite(Date.parse(data.assignedAt)))) throw new Error("Invalid assignment");
    const pilot = data.pilot;
    if (pilot && (pilot.key !== "canada-subscription-v1" || typeof pilot.assignmentId !== "string" ||
      !Number.isFinite(Date.parse(pilot.assignedAt)))) throw new Error("Invalid subscription assignment");
    setSubscriptionPilot("ready", Boolean(pilot), pilot?.assignmentId ?? null);
    // Exclusion must never leave an old account on a newly reduced cap. A
    // previously promised unlimited offer is independent and remains intact.
    const servedVariant = data.status === "excluded" && (variant === "cap5" || variant === "cap6")
      ? "control" : variant;
    // A paid treatment account may temporarily hide its free offer. That is
    // not a new ten-component promise and must not remove its own purchase
    // from the experiment on the next visit.
    const defaultTen = data.status === "excluded" && servedVariant === null &&
      !data.assignmentId && data.reason !== "internal";
    if (!pilot && (servedVariant === "control" || defaultTen)) preserveFreeAllowance();
    if (data.status === "assigned" && data.version === CONSUMER_OFFER_VERSION && subject &&
      (servedVariant === "control" || servedVariant === "cap5")) {
      setConfirmedOfferAnalytics({ subject, variant: servedVariant, assignmentId: data.assignmentId });
    }
    // The server confirms durable assignment + deduplicated enrollment capture
    // BEFORE allowing a differing price, CTA or cap to render.
    useEquipmentCapExperiment.setState({ ready: true, status: data.status,
      variant, servedVariant, subject, userId, assignmentId: data.assignmentId ?? null,
      assignedAt: data.assignedAt ?? null, version: data.version ?? null });
  } catch {
    if (current()) {
      markConsumerOfferUnavailable();
    }
  }
}
export function markConsumerOfferExposed(surface: string): void {
  const state = useEquipmentCapExperiment.getState();
  if (state.status !== "assigned" || !state.assignmentId || !state.servedVariant || state.subject !== getAnalyticsDistinctId() ||
    state.userId !== (useAuthStore.getState().user?.id ?? null)) return;
  const key = JSON.stringify([state.assignmentId, surface]);
  if (exposedThisPage.has(key)) return;
  exposedThisPage.add(key);
  captureAnalytics("offer_experiment_exposed", { experiment_key: state.version === "consumer-offer-v1"
    ? "equipment-cap-free-build-v1" : EQUIPMENT_CAP_EXPERIMENT_KEY,
    experiment_version: state.version ?? CONSUMER_OFFER_VERSION, assignment_id: state.assignmentId,
    variant: state.servedVariant, surface });
}
